// Centralized Error Tracking Service for Supabase errors

class ErrorTrackingService {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Keep last 100 errors
    this.listeners = [];
  }

  // Capture Supabase error with full context
  captureError(error, context = {}) {
    const errorEntry = {
      id: `error-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
      timestamp: new Date()?.toISOString(),
      code: error?.code || 'UNKNOWN',
      message: error?.message || 'An unknown error occurred',
      details: error?.details || null,
      hint: error?.hint || null,
      
      // Query context
      table: context?.table || 'unknown',
      operation: context?.operation || 'unknown', // SELECT, INSERT, UPDATE, DELETE
      queryParams: context?.queryParams || {},
      filters: context?.filters || {},
      
      // User session info
      userId: context?.userId || null,
      userEmail: context?.userEmail || null,
      sessionExpiry: context?.sessionExpiry || null,
      
      // Request details
      statusCode: context?.statusCode || null,
      requestHeaders: context?.requestHeaders || {},
      responseHeaders: context?.responseHeaders || {},
      
      // Additional context
      screen: context?.screen || 'unknown',
      action: context?.action || 'unknown',
      stackTrace: error?.stack || new Error()?.stack,
      executionTime: context?.executionTime || null,
      
      // RLS policy info (if available)
      policyName: this.extractPolicyName(error),
      isRLSError: this.isRLSError(error),
      
      // Full error object for debugging
      fullError: error
    };

    // Add to errors array
    this.errors?.unshift(errorEntry);
    
    // Keep only last maxErrors
    if (this.errors?.length > this.maxErrors) {
      this.errors = this.errors?.slice(0, this.maxErrors);
    }

    // Persist to localStorage
    this.persistErrors();
    
    // Notify listeners
    this.notifyListeners();
    
    // Log to console in development
    if (import.meta.env?.DEV) {
      console.error('[ErrorTracking]', errorEntry);
    }

    return errorEntry;
  }

  // Extract RLS policy name from error message
  extractPolicyName(error) {
    if (!error?.message) return null;
    
    const message = error?.message;
    
    // Common RLS error patterns
    if (message?.includes('policy')) {
      const policyMatch = message?.match(/policy "([^"]+)"/i);
      if (policyMatch) return policyMatch?.[1];
      
      const forMatch = message?.match(/for table "([^"]+)"/i);
      if (forMatch) return `RLS policy for ${forMatch?.[1]}`;
    }
    
    return null;
  }

  // Check if error is RLS-related
  isRLSError(error) {
    if (!error?.code) return false;
    
    const rlsCodes = ['42501', '42P17', 'PGRST301', 'PGRST204'];
    return rlsCodes?.includes(error?.code) || 
           error?.message?.toLowerCase()?.includes('policy') ||
           error?.message?.toLowerCase()?.includes('row-level security');
  }

  // Get all errors
  getErrors() {
    return [...this.errors];
  }

  // Get errors by screen
  getErrorsByScreen(screen) {
    return this.errors?.filter(err => err?.screen === screen);
  }

  // Get errors by table
  getErrorsByTable(table) {
    return this.errors?.filter(err => err?.table === table);
  }

  // Get RLS errors only
  getRLSErrors() {
    return this.errors?.filter(err => err?.isRLSError);
  }

  // Clear all errors
  clearErrors() {
    this.errors = [];
    this.persistErrors();
    this.notifyListeners();
  }

  // Subscribe to error changes
  subscribe(listener) {
    this.listeners?.push(listener);
    return () => {
      this.listeners = this.listeners?.filter(l => l !== listener);
    };
  }

  // Notify all listeners
  notifyListeners() {
    this.listeners?.forEach(listener => {
      try {
        listener(this.errors);
      } catch (err) {
        console.error('Error in listener:', err);
      }
    });
  }

  // Persist errors to localStorage
  persistErrors() {
    try {
      const errorsToStore = this.errors?.map(err => ({
        ...err,
        fullError: undefined, // Don't store full error object
        stackTrace: err?.stackTrace?.substring(0, 500) // Truncate stack trace
      }));
      localStorage.setItem('supabase_errors', JSON.stringify(errorsToStore));
    } catch (err) {
      console.error('Failed to persist errors:', err);
    }
  }

  // Load errors from localStorage
  loadErrors() {
    try {
      const stored = localStorage.getItem('supabase_errors');
      if (stored) {
        this.errors = JSON.parse(stored);
        this.notifyListeners();
      }
    } catch (err) {
      console.error('Failed to load errors:', err);
    }
  }

  // Get error statistics
  getStatistics() {
    const stats = {
      total: this.errors?.length,
      byCode: {},
      byTable: {},
      byScreen: {},
      rlsErrors: 0,
      last24Hours: 0
    };

    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

    this.errors?.forEach(err => {
      // By code
      stats.byCode[err.code] = (stats?.byCode?.[err?.code] || 0) + 1;
      
      // By table
      stats.byTable[err.table] = (stats?.byTable?.[err?.table] || 0) + 1;
      
      // By screen
      stats.byScreen[err.screen] = (stats?.byScreen?.[err?.screen] || 0) + 1;
      
      // RLS errors
      if (err?.isRLSError) stats.rlsErrors++;
      
      // Last 24 hours
      if (new Date(err.timestamp) > oneDayAgo) stats.last24Hours++;
    });

    return stats;
  }
}

// Create singleton instance
const errorTrackingService = new ErrorTrackingService();

// Load persisted errors on initialization
errorTrackingService?.loadErrors();

export default errorTrackingService;